import { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import {
  ContractorInterface,
  ContractorResourceTypeEnum,
} from '@vatos-pas/common';
import { CAPACITY_FIELD_MAP } from './capacityFieldMap';

export interface CapacityStatus {
  isOverCapacity: boolean;
  workload: number;
  capacity: number;
}

export function useContractorCapacityStatus(
  contractors: ContractorInterface[],
  resourceType: ContractorResourceTypeEnum | null,
  enabled: boolean,
): { statusMap: Record<string, CapacityStatus>; loading: boolean } {
  const dataProvider = useDataProvider();
  const [statusMap, setStatusMap] = useState<Record<string, CapacityStatus>>(
    {},
  );
  const [loading, setLoading] = useState(false);

  const contractorIds = contractors
    .map(c => c.id)
    .sort()
    .join(',');

  useEffect(() => {
    const capacityField = resourceType
      ? CAPACITY_FIELD_MAP[resourceType]
      : undefined;

    if (!enabled || !resourceType || !capacityField || contractorIds === '') {
      setStatusMap({});
      return;
    }

    setLoading(true);
    dataProvider
      .getList('contractor-allocated-capacity', {
        filter: {
          'contractorId||$in': contractorIds,
          'resourceType||$eq': resourceType,
        },
        pagination: { page: 1, perPage: contractors.length },
        sort: { field: 'contractorId', order: 'ASC' },
      })
      .then(({ data }) => {
        const workloadMap: Record<string, number> = {};
        data.forEach(row => {
          workloadMap[row.contractorId] = Number(row.totalAllocated);
        });

        const map: Record<string, CapacityStatus> = {};
        contractors.forEach(contractor => {
          const capacity = contractor[capacityField] ?? 0;
          const workload = workloadMap[contractor.id] ?? 0;
          map[contractor.id] = {
            isOverCapacity: capacity > 0 && workload >= capacity,
            workload,
            capacity,
          };
        });
        setStatusMap(map);
      })
      .catch(err => {
        console.error('Failed to load contractor capacity status', err);
        setStatusMap({});
      })
      .finally(() => setLoading(false));
  }, [enabled, resourceType, contractorIds]);

  return { statusMap, loading };
}
